import { SheetShapeDeepFragment } from '../../gql/graphql';
import {
  CornerMultiPolygon2Algorithm,
  ExtrudedPolygonAlgorithm,
  ExtrudedPolygonAlgorithmType,
  GeometricAlgorithmType,
  MeshAlgorithm,
  MeshAlgorithmType,
  MultiPolygon2Algorithm,
  MultiPolygon2AlgorithmType,
  MultiPolygonAlgorithm,
  MultiPolygonAlgorithmType,
  ParameterizedMultiPolygon2Algorithm,
  Point2Algorithm,
  Point2AlgorithmType,
  PointAlgorithm,
  PointAlgorithmType,
  Polyline2Algorithm,
  Polyline2AlgorithmType,
  PolylineAlgorithm,
  PolylineAlgorithmType,
  RandMultiPolygon2Algorithm,
  SlopedInsulationAlgorithm,
} from './algorithm-types';
import { calculateBottomSurfaceArea } from './algorithms/bottom-surface-area';
import { calculateBoundingBoxLength } from './algorithms/bounding-box-length';
import { calculateBoundingBoxWidth } from './algorithms/bounding-box-width';
import { calculateExtrudedShapeSideArea } from './algorithms/extruded-shape-side-area';
import { calculateExtrudedShapeThickness } from './algorithms/extruded-shape-thickness';
import { calculateExtrudedShapeVolume } from './algorithms/extruded-shape-volume';
import { calculateGrossSideAreaConvexHull } from './algorithms/gross-side-area-convex-hull';
import { calculateLargestFlatSurfaceArea } from './algorithms/largest-flat-surface';
import { calculateLargestSurfaceArea } from './algorithms/largest-surface-area';
import { calculateLargestSurfaceAreaWithoutOpenings } from './algorithms/largest-surface-area-without-openings';
import { calculateLineLength } from './algorithms/line-length';
import { calculateLine2Length } from './algorithms/line2-length';
import { calculatePerimeter } from './algorithms/perimeter';
import { calculateShapeArea } from './algorithms/shape-area';
import { calculateShapeBoundingBoxWidth } from './algorithms/shape-bounding-box-width';
import { calculateShapeBoundingBoxLength } from './algorithms/shape-bounding-box-length';
import { calculateShapeGrossSideArea } from './algorithms/shape-gross-side-area';
import { calculateShapePerimeter } from './algorithms/shape-perimeter';
import { calculateShapeVerticalComponent } from './algorithms/shape-vertical-component';
import { calculateSheetShapeArea } from './algorithms/sheet-shape-area';
import { calculateSheetShapeCornerAreas } from './algorithms/sheet-shape-corner';
import { calculateSheetShapeFireSectioning } from './algorithms/sheet-shape-fire-sectioning';
import { calculateSheetShapeGrossSideArea } from './algorithms/sheet-shape-gross-side-area';
import { calculateSheetShapePerimeter } from './algorithms/sheet-shape-perimeter';
import { calculateSheetShapeRandfelt } from './algorithms/sheet-shape-randfelt';
import { calculateSummedTetrahedronVolume } from './algorithms/summed-tetrahedron-volume';
import { calculateTopSurfaceArea } from './algorithms/top-surface-area';
import { calculateTotalSurfaceArea } from './algorithms/total-surface-area';
import { calculateVerticalComponent } from './algorithms/vertical-component';
import {
  MultiPolygon,
  MultiPolygon2,
  Plane,
  Point,
  Point2,
  Triangle,
} from './geometric-types';
import { calculateLine2BaselineLength } from './algorithms/line2-baseline-length';
import { calculateSheetShapeBoundingBoxWidth } from './algorithms/sheet-shape-bounding-box-width';
import { calculateSheetShapeBoundingBoxLength } from './algorithms/sheet-shape-bounding-box-length';
import {
  BoundingBoxPosition,
  calculateBoundingBox,
} from './algorithms/bounding-box';
import {
  calculateLineShapeBoundingBox,
  calculateShapeBoundingBox,
} from './algorithms/shape-bounding-box';
import {
  BoundingBox2Position,
  calculateSheetLineShapeBoundingBox,
  calculateSheetPolygonShapeBoundingBox,
} from './algorithms/sheet-shape-bounding-box';
import { calculateSheetShapeInternalPerimeter } from './algorithms/sheet-shape-internal-perimeter';
import { calculateSheetShapeNumberOfExternalCorners } from './algorithms/sheet-shape-number-of-external-corners';
import { SlopedInsulationCrossSection } from 'src/components/orderContractor/pdf-shapes/SheetShapeDrawing';

// eslint-disable-next-line complexity
export function getGeometricAlgorithmForType(
  algorithmType: GeometricAlgorithmType,
  projectSettings?: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  }
):
  | MeshAlgorithm
  | MultiPolygonAlgorithm
  | ExtrudedPolygonAlgorithm
  | PolylineAlgorithm
  | PointAlgorithm
  | MultiPolygon2Algorithm
  | Polyline2Algorithm
  | Point2Algorithm
  | ParameterizedMultiPolygon2Algorithm
  | CornerMultiPolygon2Algorithm
  | RandMultiPolygon2Algorithm
  | SlopedInsulationAlgorithm {
  const globalOffset = projectSettings?.globalOffset || [0, 0, 0];

  switch (algorithmType) {
    case MeshAlgorithmType.BOUNDING_BOX_LENGTH:
      return (triangles: Triangle[]) =>
        calculateBoundingBoxLength(triangles, false);
    case MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH:
      return (triangles: Triangle[]) =>
        calculateBoundingBoxLength(triangles, true);
    case MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH:
      return calculateBoundingBoxWidth;
    case MeshAlgorithmType.VERTICAL_COMPONENT:
      return calculateVerticalComponent;
    case MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA:
      return (
        triangles: Triangle[],
        maxMatchDistancePlanes?: number | undefined
      ) =>
        triangles.length >
        (projectSettings?.maxTrianglesForComplexAlgorithm || 5000)
          ? undefined
          : calculateGrossSideAreaConvexHull(triangles, maxMatchDistancePlanes);
    case MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA_INFINITY:
      return (triangles: Triangle[]) =>
        triangles.length >
        (projectSettings?.maxTrianglesForComplexAlgorithm || 5000)
          ? undefined
          : calculateGrossSideAreaConvexHull(triangles, Infinity);
    case MeshAlgorithmType.TOTAL_MESH_AREA:
      return (triangles: Triangle[]) =>
        triangles.length >
        (projectSettings?.maxTrianglesForComplexAlgorithm || 5000)
          ? undefined
          : calculateTotalSurfaceArea(triangles);
    case MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA:
      return (triangles: Triangle[]) =>
        triangles.length >
        (projectSettings?.maxTrianglesForComplexAlgorithm || 5000)
          ? undefined
          : calculateLargestFlatSurfaceArea(triangles);
    case MeshAlgorithmType.LARGEST_SURFACE_AREA:
      return (triangles: Triangle[]) =>
        triangles.length >
        (projectSettings?.maxTrianglesForComplexAlgorithm || 5000)
          ? undefined
          : calculateLargestSurfaceArea(triangles);
    case MeshAlgorithmType.LARGEST_SURFACE_AREA_WITHOUT_OPENINGS:
      return (triangles: Triangle[]) =>
        triangles.length >
        (projectSettings?.maxTrianglesForComplexAlgorithm || 5000)
          ? undefined
          : calculateLargestSurfaceAreaWithoutOpenings(triangles);
    case MeshAlgorithmType.TETRAHEDRON_VOLUME:
      return (triangles: Triangle[]) =>
        triangles.length >
        (projectSettings?.maxTrianglesForComplexAlgorithm || 5000)
          ? undefined
          : calculateSummedTetrahedronVolume(triangles);
    case MeshAlgorithmType.TOP_SURFACE_AREA:
      return (triangles: Triangle[]) =>
        triangles.length >
        (projectSettings?.maxTrianglesForComplexAlgorithm || 5000)
          ? undefined
          : calculateTopSurfaceArea(triangles);
    case MeshAlgorithmType.BOTTOM_SURFACE_AREA:
      return calculateBottomSurfaceArea;
    case MeshAlgorithmType.MESH_PERIMETER:
      return (triangles: Triangle[]) =>
        triangles.length >
        (projectSettings?.maxTrianglesForComplexAlgorithm || 5000)
          ? undefined
          : calculatePerimeter(triangles);
    case MeshAlgorithmType.MESH_LOCATION_MIN_X:
      return (triangles: Triangle[]) =>
        calculateBoundingBox(
          triangles,
          BoundingBoxPosition.MIN_X,
          globalOffset
        );
    case MeshAlgorithmType.MESH_LOCATION_MIN_Y:
      return (triangles: Triangle[]) =>
        calculateBoundingBox(
          triangles,
          BoundingBoxPosition.MIN_Y,
          globalOffset
        );
    case MeshAlgorithmType.MESH_LOCATION_MIN_Z:
      return (triangles: Triangle[]) =>
        calculateBoundingBox(
          triangles,
          BoundingBoxPosition.MIN_Z,
          globalOffset
        );
    case MeshAlgorithmType.MESH_LOCATION_MAX_X:
      return (triangles: Triangle[]) =>
        calculateBoundingBox(
          triangles,
          BoundingBoxPosition.MAX_X,
          globalOffset
        );
    case MeshAlgorithmType.MESH_LOCATION_MAX_Y:
      return (triangles: Triangle[]) =>
        calculateBoundingBox(
          triangles,
          BoundingBoxPosition.MAX_Y,
          globalOffset
        );
    case MeshAlgorithmType.MESH_LOCATION_MAX_Z:
      return (triangles: Triangle[]) =>
        calculateBoundingBox(
          triangles,
          BoundingBoxPosition.MAX_Z,
          globalOffset
        );

    case MultiPolygonAlgorithmType.POLYGON_VERTICAL_COMPONENT:
      return calculateShapeVerticalComponent;
    case MultiPolygonAlgorithmType.POLYGON_GROSS_SIDE_AREA:
      return calculateShapeGrossSideArea;
    case MultiPolygonAlgorithmType.POLYGON_AREA:
      return calculateShapeArea;
    case MultiPolygonAlgorithmType.POLYGON_PERIMETER:
      return calculateShapePerimeter;
    case MultiPolygonAlgorithmType.POLYGON_BOUNDING_BOX_WIDTH:
      return calculateShapeBoundingBoxWidth;
    case MultiPolygonAlgorithmType.POLYGON_BOUNDING_BOX_LENGTH:
      return calculateShapeBoundingBoxLength;
    case MultiPolygonAlgorithmType.POLYGON_LOCATION_MIN_X:
      return (polygon: MultiPolygon, plane: Plane) =>
        calculateShapeBoundingBox(
          polygon,
          plane,
          BoundingBoxPosition.MIN_X,
          globalOffset
        );
    case MultiPolygonAlgorithmType.POLYGON_LOCATION_MIN_Y:
      return (polygon: MultiPolygon, plane: Plane) =>
        calculateShapeBoundingBox(
          polygon,
          plane,
          BoundingBoxPosition.MIN_Y,
          globalOffset
        );
    case MultiPolygonAlgorithmType.POLYGON_LOCATION_MIN_Z:
      return (polygon: MultiPolygon, plane: Plane) =>
        calculateShapeBoundingBox(
          polygon,
          plane,
          BoundingBoxPosition.MIN_Z,
          globalOffset
        );
    case MultiPolygonAlgorithmType.POLYGON_LOCATION_MAX_X:
      return (polygon: MultiPolygon, plane: Plane) =>
        calculateShapeBoundingBox(
          polygon,
          plane,
          BoundingBoxPosition.MAX_X,
          globalOffset
        );
    case MultiPolygonAlgorithmType.POLYGON_LOCATION_MAX_Y:
      return (polygon: MultiPolygon, plane: Plane) =>
        calculateShapeBoundingBox(
          polygon,
          plane,
          BoundingBoxPosition.MAX_Y,
          globalOffset
        );
    case MultiPolygonAlgorithmType.POLYGON_LOCATION_MAX_Z:
      return (polygon: MultiPolygon, plane: Plane) =>
        calculateShapeBoundingBox(
          polygon,
          plane,
          BoundingBoxPosition.MAX_Z,
          globalOffset
        );

    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_THICKNESS:
      return calculateExtrudedShapeThickness;
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_VOLUME:
      return calculateExtrudedShapeVolume;
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_AREA:
      return calculateShapeArea;
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_PERIMETER:
      return calculateShapePerimeter;
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_SIDE_AREA:
      return calculateExtrudedShapeSideArea;
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_BOUNDING_BOX_WIDTH:
      return calculateShapeBoundingBoxWidth;
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_BOUNDING_BOX_LENGTH:
      return calculateShapeBoundingBoxLength;
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MIN_X:
      return (polygon: MultiPolygon, plane: Plane, thickness?: number) =>
        calculateShapeBoundingBox(
          polygon,
          plane,
          BoundingBoxPosition.MIN_X,
          globalOffset,
          thickness
        );
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MIN_Y:
      return (polygon: MultiPolygon, plane: Plane, thickness?: number) =>
        calculateShapeBoundingBox(
          polygon,
          plane,
          BoundingBoxPosition.MIN_Y,
          globalOffset,
          thickness
        );
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MIN_Z:
      return (polygon: MultiPolygon, plane: Plane, thickness?: number) =>
        calculateShapeBoundingBox(
          polygon,
          plane,
          BoundingBoxPosition.MIN_Z,
          globalOffset,
          thickness
        );
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MAX_X:
      return (polygon: MultiPolygon, plane: Plane, thickness?: number) =>
        calculateShapeBoundingBox(
          polygon,
          plane,
          BoundingBoxPosition.MAX_X,
          globalOffset,
          thickness
        );
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MAX_Y:
      return (polygon: MultiPolygon, plane: Plane, thickness?: number) =>
        calculateShapeBoundingBox(
          polygon,
          plane,
          BoundingBoxPosition.MAX_Y,
          globalOffset,
          thickness
        );
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MAX_Z:
      return (polygon: MultiPolygon, plane: Plane, thickness?: number) =>
        calculateShapeBoundingBox(
          polygon,
          plane,
          BoundingBoxPosition.MAX_Z,
          globalOffset,
          thickness
        );

    case PolylineAlgorithmType.LINE_LENGTH:
      return calculateLineLength;
    case PolylineAlgorithmType.LINE_LOCATION_MIN_X:
      return (points: Point[]) =>
        calculateLineShapeBoundingBox(
          points,
          BoundingBoxPosition.MIN_X,
          globalOffset
        );
    case PolylineAlgorithmType.LINE_LOCATION_MIN_Y:
      return (points: Point[]) =>
        calculateLineShapeBoundingBox(
          points,
          BoundingBoxPosition.MIN_Y,
          globalOffset
        );
    case PolylineAlgorithmType.LINE_LOCATION_MIN_Z:
      return (points: Point[]) =>
        calculateLineShapeBoundingBox(
          points,
          BoundingBoxPosition.MIN_Z,
          globalOffset
        );
    case PolylineAlgorithmType.LINE_LOCATION_MAX_X:
      return (points: Point[]) =>
        calculateLineShapeBoundingBox(
          points,
          BoundingBoxPosition.MAX_X,
          globalOffset
        );
    case PolylineAlgorithmType.LINE_LOCATION_MAX_Y:
      return (points: Point[]) =>
        calculateLineShapeBoundingBox(
          points,
          BoundingBoxPosition.MAX_Y,
          globalOffset
        );
    case PolylineAlgorithmType.LINE_LOCATION_MAX_Z:
      return (points: Point[]) =>
        calculateLineShapeBoundingBox(
          points,
          BoundingBoxPosition.MAX_Z,
          globalOffset
        );

    case PointAlgorithmType.POINT_LOCATION_X:
      return (point: Point) =>
        calculateLineShapeBoundingBox(
          [point],
          BoundingBoxPosition.MIN_X,
          globalOffset
        );
    case PointAlgorithmType.POINT_LOCATION_Y:
      return (point: Point) =>
        calculateLineShapeBoundingBox(
          [point],
          BoundingBoxPosition.MIN_Y,
          globalOffset
        );
    case PointAlgorithmType.POINT_LOCATION_Z:
      return (point: Point) =>
        calculateLineShapeBoundingBox(
          [point],
          BoundingBoxPosition.MIN_Z,
          globalOffset
        );

    case MultiPolygon2AlgorithmType.POLYGON2_PERIMETER:
      return calculateSheetShapePerimeter;
    case MultiPolygon2AlgorithmType.POLYGON2_INTERNAL_PERIMETER:
      return calculateSheetShapeInternalPerimeter;
    case MultiPolygon2AlgorithmType.POLYGON2_NUMBER_OF_EXTERNAL_CORNERS:
      return calculateSheetShapeNumberOfExternalCorners;
    case MultiPolygon2AlgorithmType.POLYGON2_AREA:
      return calculateSheetShapeArea;
    case MultiPolygon2AlgorithmType.POLYGON2_FIRE_SECTIONING:
      return (
        multiPolygonInput: MultiPolygon2,
        buffer: number,
        externalShapes: SheetShapeDeepFragment[]
      ) =>
        calculateSheetShapeFireSectioning(
          multiPolygonInput,
          buffer,
          externalShapes
        );
    case MultiPolygon2AlgorithmType.POLYGON2_CORNER_AREA:
      return (
        multiPolygonInput: MultiPolygon2,
        roofHeight: number | undefined,
        unitScale: number
      ) =>
        calculateSheetShapeCornerAreas(
          multiPolygonInput,
          roofHeight,
          unitScale
        );
    case MultiPolygon2AlgorithmType.POLYGON2_RAND_AREA:
      return (
        multiPolygonInput: MultiPolygon2,
        roofHeight: number | undefined,
        unitScale: number
      ) =>
        calculateSheetShapeRandfelt(multiPolygonInput, roofHeight, unitScale);
    case MultiPolygon2AlgorithmType.POLYGON2_GROSS_SIDE_AREA:
      return calculateSheetShapeGrossSideArea;
    case MultiPolygon2AlgorithmType.POLYGON2_BOUNDING_BOX_WIDTH:
      return calculateSheetShapeBoundingBoxWidth;
    case MultiPolygon2AlgorithmType.POLYGON2_BOUNDING_BOX_LENGTH:
      return calculateSheetShapeBoundingBoxLength;
    case MultiPolygon2AlgorithmType.POLYGON2_LOCATION_MIN_X:
      return (multiPolygonInput: MultiPolygon2) =>
        calculateSheetPolygonShapeBoundingBox(
          multiPolygonInput,
          BoundingBox2Position.MIN_X
        );
    case MultiPolygon2AlgorithmType.POLYGON2_LOCATION_MIN_Y:
      return (multiPolygonInput: MultiPolygon2) =>
        calculateSheetPolygonShapeBoundingBox(
          multiPolygonInput,
          BoundingBox2Position.MIN_Y
        );
    case MultiPolygon2AlgorithmType.POLYGON2_LOCATION_MAX_X:
      return (multiPolygonInput: MultiPolygon2) =>
        calculateSheetPolygonShapeBoundingBox(
          multiPolygonInput,
          BoundingBox2Position.MAX_X
        );
    case MultiPolygon2AlgorithmType.POLYGON2_LOCATION_MAX_Y:
      return (multiPolygonInput: MultiPolygon2) =>
        calculateSheetPolygonShapeBoundingBox(
          multiPolygonInput,
          BoundingBox2Position.MAX_Y
        );

    case Polyline2AlgorithmType.LINE2_LENGTH:
      return calculateLine2Length;
    case Polyline2AlgorithmType.LINE2_CROSS_SECTION_BASELINE_LENGTH:
      return (crossSection: SlopedInsulationCrossSection) =>
        calculateLine2BaselineLength(crossSection);
    case Polyline2AlgorithmType.LINE2_CROSS_SECTION_DIAGONAL_LENGTH:
      return calculateLine2Length;
    case Polyline2AlgorithmType.LINE2_LOCATION_MIN_X:
      return (points: Point2[]) =>
        calculateSheetLineShapeBoundingBox(points, BoundingBox2Position.MIN_X);
    case Polyline2AlgorithmType.LINE2_LOCATION_MIN_Y:
      return (points: Point2[]) =>
        calculateSheetLineShapeBoundingBox(points, BoundingBox2Position.MIN_Y);
    case Polyline2AlgorithmType.LINE2_LOCATION_MAX_X:
      return (points: Point2[]) =>
        calculateSheetLineShapeBoundingBox(points, BoundingBox2Position.MAX_X);
    case Polyline2AlgorithmType.LINE2_LOCATION_MAX_Y:
      return (points: Point2[]) =>
        calculateSheetLineShapeBoundingBox(points, BoundingBox2Position.MAX_Y);

    case Point2AlgorithmType.POINT2_LOCATION_X:
      return (point: Point2) =>
        calculateSheetLineShapeBoundingBox([point], BoundingBox2Position.MIN_X);
    case Point2AlgorithmType.POINT2_LOCATION_Y:
      return (point: Point2) =>
        calculateSheetLineShapeBoundingBox([point], BoundingBox2Position.MIN_Y);
  }
}
